import React, { useRef, useEffect } from 'react';
import { resolveMediaUrl } from '../utils/styleHelpers.js';

/**
 * GradientAnimator - Scroll-driven infinite continuous gradient animation
 * props:
 *   prefix   – 'page' | 'hero'  (to read settings keys)
 *   settings – the site settings object
 *   customObj – optional custom section/hero object
 *   fixed    – true = position:fixed (page bg), false = position:absolute (hero section)
 */
export const GradientAnimator = ({ prefix, settings, customObj, fixed = true }) => {
    const divRef = useRef(null);
    const overlayRef = useRef(null);

    const start = customObj ? (customObj.heroGradientStart || customObj.gradientStart || (customObj.backgroundColor && customObj.backgroundColor.startsWith('#') ? customObj.backgroundColor : '#2a0a0a')) : (settings?.[`${prefix}GradientStart`] || '#2a0a0a');
    const mid = customObj ? (customObj.heroGradientMid || customObj.gradientMid || null) : (settings?.[`${prefix}GradientMid`] || null);
    const end = customObj ? (customObj.heroGradientEnd || customObj.gradientEnd || '#1a0505') : (settings?.[`${prefix}GradientEnd`] || '#1a0505');
    const angle = customObj ? (customObj.heroGradientAngle || customObj.gradientAngle || '180') : (settings?.[`${prefix}GradientAngle`] || '180');
    const speed = parseFloat(customObj ? (customObj.heroGradientAnimateSpeed || customObj.gradientAnimateSpeed || '8') : (settings?.[`${prefix}GradientAnimateSpeed`] || '8'));
    const mode = customObj ? (customObj.heroGradientAnimateMode || customObj.gradientAnimateMode || 'translate') : (settings?.[`${prefix}GradientAnimateMode`] || 'translate');

    // Single seamless tile stops: starts and ends at 'start' so browser native background-repeat tiles seamlessly
    const stops = mid
        ? `${start} 0%, ${mid} 25%, ${end} 50%, ${mid} 75%, ${start} 100%`
        : `${start} 0%, ${end} 50%, ${start} 100%`;

    const gradient = `linear-gradient(${angle}deg, ${stops})`;

    // Secondary inverted gradient for opacity cross-fading
    const reverseAngle = (parseFloat(angle) + 180) % 360;
    const overlayStops = mid
        ? `${end} 0%, ${mid} 25%, ${start} 50%, ${mid} 75%, ${end} 100%`
        : `${end} 0%, ${start} 50%, ${end} 100%`;
    const overlayGradient = `linear-gradient(${reverseAngle}deg, ${overlayStops})`;

    useEffect(() => {
        let animFrame;
        let ticking = false;
        let startTime = null;

        if (mode === 'scroll') {
            const el = divRef.current;
            if (!el) return;

            const updatePosition = () => {
                const scrollY = window.scrollY || window.pageYOffset || 0;
                const speedVal = speed > 0 ? speed : 8;
                const yOffset = -(scrollY / speedVal);
                el.style.backgroundPositionY = `${yOffset.toFixed(2)}px`;
                ticking = false;
            };

            const onScroll = () => {
                if (!ticking) {
                    requestAnimationFrame(updatePosition);
                    ticking = true;
                }
            };

            window.addEventListener('scroll', onScroll, { passive: true });
            updatePosition();

            return () => window.removeEventListener('scroll', onScroll);
        } else if (mode === 'translate') {
            const el = divRef.current;
            if (!el) return;

            const cycleDuration = Math.max(3, 35 - speed) * 1000;

            const step = (timestamp) => {
                if (!startTime) startTime = timestamp;
                const elapsed = (timestamp - startTime) % cycleDuration;
                const progress = elapsed / cycleDuration;
                const yPercent = Math.sin(progress * Math.PI * 2) * 6;
                el.style.transform = `translate3d(0, ${yPercent.toFixed(2)}%, 0)`;
                animFrame = requestAnimationFrame(step);
            };

            animFrame = requestAnimationFrame(step);
            return () => {
                if (animFrame) cancelAnimationFrame(animFrame);
            };
        } else if (mode === 'opacity') {
            const el = overlayRef.current;
            if (!el) return;

            const cycleDuration = Math.max(2, 30 - speed) * 1000;

            const step = (timestamp) => {
                if (!startTime) startTime = timestamp;
                const elapsed = (timestamp - startTime) % cycleDuration;
                const progress = elapsed / cycleDuration;
                const opacityVal = 0.1 + (Math.sin(progress * Math.PI * 2) + 1) * 0.4;
                el.style.opacity = opacityVal.toFixed(3);
                animFrame = requestAnimationFrame(step);
            };

            animFrame = requestAnimationFrame(step);
            return () => {
                if (animFrame) cancelAnimationFrame(animFrame);
            };
        }
    }, [mode, speed, gradient]);

    const isTranslate = mode === 'translate';

    return (
        <div
            style={{
                position: fixed ? 'fixed' : 'absolute',
                inset: isTranslate ? '-15% 0' : 0,
                zIndex: fixed ? -1 : 0,
                overflow: 'hidden',
                pointerEvents: 'none',
            }}
        >
            <div
                ref={divRef}
                style={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: gradient,
                    backgroundSize: mode === 'scroll' ? '100% 100vh' : '100% 100%',
                    backgroundRepeat: mode === 'scroll' ? 'repeat' : 'no-repeat',
                    backgroundPosition: '0px 0px',
                    willChange: isTranslate ? 'transform' : (mode === 'scroll' ? 'background-position' : undefined),
                    backfaceVisibility: 'hidden',
                    WebkitBackfaceVisibility: 'hidden',
                }}
            />
            {mode === 'opacity' && (
                <div
                    ref={overlayRef}
                    style={{
                        position: 'absolute',
                        inset: 0,
                        backgroundImage: overlayGradient,
                        backgroundSize: '100% 100%',
                        opacity: 0.1,
                        willChange: 'opacity',
                        backfaceVisibility: 'hidden',
                        WebkitBackfaceVisibility: 'hidden',
                    }}
                />
            )}
        </div>
    );
};

const EASING_LERP_MAP = {
    'cubic-bezier(0.16, 1, 0.3, 1)': 0.20, // Smooth Spring - silky smooth & responsive
    'ease-out': 0.28,                       // Quick & snappy settle
    'ease-in-out': 0.18,                    // Soft organic deceleration
    'linear': 1.0,                          // Direct 1:1 scroll tracking (zero lag)
    'cubic-bezier(0.34, 1.56, 0.64, 1)': 0.16, // Bounce / Overshoot
    'cubic-bezier(0.68, -0.6, 0.32, 1.6)': 0.14 // Elastic Back Out
};

const resolveParallaxEase = (val) => {
    if (typeof val === 'string' && EASING_LERP_MAP[val] !== undefined) {
        return EASING_LERP_MAP[val];
    }
    const num = parseFloat(val);
    if (!isNaN(num) && num > 0) {
        return Math.max(0.05, Math.min(1.0, num));
    }
    return 0.20;
};

export const PageParallaxLayer = ({ bgImage, bgSize, bgPosition, bgRepeat, speed = 0.5, smoothing = 'cubic-bezier(0.16, 1, 0.3, 1)', bgFilter, bgDesaturate, bgTint, bgTintOpacity, bgTintBlendMode }) => {
    const bgRef = useRef(null);
    const currentYRef = useRef(0);
    const targetYRef = useRef(0);

    const isMobile = typeof window !== 'undefined' && (window.innerWidth <= 768 || ('ontouchstart' in window && window.innerWidth <= 1024));
    const speedVal = parseFloat(speed ?? 0.5);
    const easeVal = resolveParallaxEase(smoothing);
    // On mobile devices, background-attachment: fixed causes severe rendering bugs/jitter in WebKit/Blink; use GPU translate3d parallax instead
    const useFixed = !isMobile && (speedVal === 1 || speedVal === 1.0);

    const mobileScale = isMobile ? 0.65 : 1.0;
    const maxTravelPx = Math.min(260, Math.max(35, Math.round(speedVal * 140 * mobileScale)));
    const overscanPx = !useFixed ? Math.round(maxTravelPx * 1.15 + 12) : 0;

    useEffect(() => {
        if (useFixed) {
            if (bgRef.current) bgRef.current.style.transform = '';
            return;
        }
        const bg = bgRef.current;
        if (!bg) return;
        let animationFrameId;
        let isAnimating = false;

        const tick = () => {
            if (!bg) {
                isAnimating = false;
                return;
            }

            if (easeVal >= 0.95) {
                // Instant / Direct 1:1 response (Linear)
                currentYRef.current = targetYRef.current;
                bg.style.transform = `translate3d(0, ${currentYRef.current.toFixed(2)}px, 0)`;
                isAnimating = false;
                return;
            }

            const diff = targetYRef.current - currentYRef.current;
            if (Math.abs(diff) > 0.04) {
                currentYRef.current += diff * easeVal;
                bg.style.transform = `translate3d(0, ${currentYRef.current.toFixed(2)}px, 0)`;
                animationFrameId = requestAnimationFrame(tick);
            } else {
                currentYRef.current = targetYRef.current;
                bg.style.transform = `translate3d(0, ${currentYRef.current.toFixed(2)}px, 0)`;
                isAnimating = false;
            }
        };

        const updateParallax = () => {
            const scrollY = window.scrollY || window.pageYOffset || 0;
            const vh = window.innerHeight || 800;

            if (bgRepeat === 'repeat' || bgRepeat === 'repeat-y') {
                targetYRef.current = -(scrollY * speedVal * 0.25);
            } else {
                const scrollSpan = Math.max(vh * 2, 1000);
                const progress = Math.min(1, Math.max(-1, (scrollY / scrollSpan) * 2 - 1));
                targetYRef.current = progress * maxTravelPx * (speedVal < 1 ? -1 : 1);
            }

            if (!isAnimating) {
                isAnimating = true;
                animationFrameId = requestAnimationFrame(tick);
            }
        };

        window.addEventListener('scroll', updateParallax, { passive: true });
        window.addEventListener('resize', updateParallax, { passive: true });
        updateParallax();

        return () => {
            window.removeEventListener('scroll', updateParallax);
            window.removeEventListener('resize', updateParallax);
            if (animationFrameId) cancelAnimationFrame(animationFrameId);
        };
    }, [useFixed, easeVal, maxTravelPx, speedVal, bgRepeat]);

    const resolvedBg = resolveMediaUrl(bgImage);

    const computedFilter = (() => {
        const parts = [];
        const desat = parseInt(bgDesaturate ?? 0, 10);
        if (!isNaN(desat) && desat > 0) parts.push(`grayscale(${Math.min(100, Math.max(0, desat))}%)`);
        if (bgFilter && bgFilter !== 'none') parts.push(bgFilter);
        return parts.length > 0 ? parts.join(' ') : undefined;
    })();

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: -1, overflow: 'hidden', pointerEvents: 'none' }}>
            <div
                ref={bgRef}
                style={{
                    position: 'absolute',
                    top: !useFixed ? `-${overscanPx}px` : 0,
                    left: 0,
                    right: 0,
                    bottom: !useFixed ? `-${overscanPx}px` : 0,
                    height: !useFixed ? `calc(100% + ${overscanPx * 2}px)` : '100%',
                    backgroundImage: resolvedBg ? `url('${resolvedBg}')` : 'none',
                    backgroundAttachment: useFixed ? 'fixed' : 'scroll',
                    backgroundSize: bgSize || 'cover',
                    backgroundPosition: bgPosition || 'center',
                    backgroundRepeat: bgRepeat || 'no-repeat',
                    willChange: !useFixed ? 'transform' : undefined,
                    backfaceVisibility: 'hidden',
                    WebkitBackfaceVisibility: 'hidden',
                    ...(computedFilter ? { filter: computedFilter, WebkitFilter: computedFilter } : {})
                }}
            />
            {Boolean(bgTint && bgTint !== 'transparent') && (
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        backgroundColor: bgTint,
                        opacity: bgTintOpacity !== undefined ? parseFloat(bgTintOpacity) : 0.4,
                        mixBlendMode: bgTintBlendMode || 'multiply',
                        pointerEvents: 'none'
                    }}
                />
            )}
        </div>
    );
};

export default GradientAnimator;
